import { gql } from '@apollo/client'
import { BigNumber } from '@ethersproject/bignumber'
import { arrayify } from '@ethersproject/bytes'
import { keccak256 } from '@ethersproject/keccak256'
import { Trans } from '@lingui/macro'
import { formatNumber, formatNumberOrString, NumberType } from '@x-swap-protocol/conedison/format'
import { useWeb3React } from '@web3-react/core'
import { Percent } from '@x-swap-protocol/sdk-core'
import UNISWAP_V3_STAKER_ABI from 'abis/UniswapV3Staker.json'
import { useToggleAccountDrawer } from 'components/AccountDrawer'
import { useV3ManagerContracts } from 'components/AccountDrawer/MiniPortfolio/Pools/hooks'
import Badge from 'components/Badge'
import { ButtonPurple } from 'components/Button'
import { AutoColumn } from 'components/Column'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import Row, { RowBetween, RowFixed } from 'components/Row'
import { SwitchLocaleLink } from 'components/SwitchLocaleLink'
import { MouseoverTooltip } from 'components/Tooltip'
import { UNISWAP_V3_STAKER_ADDRESS } from 'constants/addresses'
import { isSupportedChain, SupportedChainId } from 'constants/chains'
import { format } from 'date-fns'
import { formatEther, formatUnits, Interface, parseUnits } from 'ethers/lib/utils'
import { apolloClient, apolloStakerClient } from 'graphql/thegraph/apollo'
import { useToken } from 'hooks/Tokens'
import { getUniswapV3Pool, useUniswapV3Staker } from 'hooks/useContract'
import { useEthPrices } from 'hooks/useEthPrices'
import { useFilterPossiblyMaliciousPositions } from 'hooks/useFilterPossiblyMaliciousPositions'
import { useV3Positions, useV3PositionsFromTokenIds } from 'hooks/useV3Positions'
import { TitleRow } from 'nft/components/profile/list/shared'
import { LoadingRows } from 'pages/Pool/styleds'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Inbox, Info } from 'react-feather'
import { Link, useParams } from 'react-router-dom'
import { fetchIncentives, fetchPools } from 'state/farm/actions'
import { useFarmIncentives, useFarmPools } from 'state/farm/hooks'
import { useAppDispatch } from 'state/hooks'
import { Status } from 'state/interfaces'
import { useCombinedActiveList } from 'state/lists/hooks'
import { useTransactionAdderWithCallback } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import styled, { css, useTheme } from 'styled-components/macro'
import { ExternalLink, MEDIA_WIDTHS, ThemedText } from 'theme'
import { PositionDetails } from 'types/position'
import { getFormattedTime } from 'utils/date'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'
import { unwrappedToken } from 'utils/unwrappedToken'

import { farms, POOLS_SEARCH, STAKED_POSITIONS, STAKER_DETAILS, UNSTAKED_POSITIONS } from '../constants'
import { IRewardInfo } from '../interfaces'
import { convertToFeeTier, encodeIncentive } from '../utils'
import { InfoModal } from './InfoModal'
import PositionList from './PositionList'

const MainContentWrapper = styled.main`
  background-color: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  padding: 0;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.01), 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 16px 24px rgba(0, 0, 0, 0.04),
    0px 24px 32px rgba(0, 0, 0, 0.01);
  overflow: hidden;
  margin-bottom: 2rem;
`

const InfoIconContainer = styled.div`
  margin-left: 2px;
  display: flex;
  align-items: center;
  cursor: help;
`

const ErrorContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  max-width: 375px;
  padding: 30px 0 32px;
`

const PageWrapper = styled(AutoColumn)`
  padding: 68px 8px 0px;
  max-width: 870px;
  width: 100%;

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToMedium`
    max-width: 800px;
  `};

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    max-width: 500px;
  `};

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.md}px`}) {
    padding-top: 48px;
  }

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.sm}px`}) {
    padding-top: 20px;
  }
`

const DesktopPoolSummaryHeader = styled.div`
  display: none;
  font-size: 14px;
  font-weight: 500;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};

  @media screen and (min-width: ${MEDIA_WIDTHS.deprecated_upToSmall}px) {
    align-items: center;
    display: flex;
    justify-content: space-between;
    & > div:last-child {
      text-align: right;
      margin-right: 12px;
    }
  }
`

const PoolSummary = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 500;
  padding: 16px;
  & > div {
    flex-basis: 100px;
  }
  & > div:last-child {
    flex-basis: 150px;
  }
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToExtraSmall`
  flex-wrap: wrap;
  gap: 20px;

  & > div {
    flex-basis: 125px;
  }
  & > div:last-child {
    flex-basis: 125px;
  }
  `};
`

function WrongNetworkCard() {
  const theme = useTheme()

  return (
    <>
      <PageWrapper>
        <AutoColumn gap="lg" justify="center">
          <AutoColumn gap="lg" style={{ width: '100%' }}>
            <TitleRow padding="0">
              <ThemedText.LargeHeader>
                <Trans>Pools</Trans>
              </ThemedText.LargeHeader>
            </TitleRow>

            <MainContentWrapper>
              <ErrorContainer>
                <ThemedText.DeprecatedBody color={theme.textTertiary} textAlign="center">
                  <NetworkIcon strokeWidth={1.2} />
                  <div data-testid="pools-unsupported-err">
                    <Trans>Your connected network is unsupported.</Trans>
                  </div>
                </ThemedText.DeprecatedBody>
              </ErrorContainer>
            </MainContentWrapper>
          </AutoColumn>
        </AutoColumn>
      </PageWrapper>
      <SwitchLocaleLink />
    </>
  )
}

const IconStyle = css`
  width: 48px;
  height: 48px;
  margin-bottom: 0.5rem;
`

const HoverText = styled(ThemedText.DeprecatedMain)`
  text-decoration: none;
  color: ${({ theme }) => (theme.darkMode ? theme.textSecondary : theme.black2)};
  :hover {
    color: ${({ theme }) => theme.textPrimary};
    text-decoration: none;
  }
`

const NetworkIcon = styled(AlertTriangle)`
  ${IconStyle}
`

const InboxIcon = styled(Inbox)`
  ${IconStyle}
`

// const ButtonRow = styled(RowFixed)`
//   & > *:not(:last-child) {
//     margin-left: 8px;
//   }

//   ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
//     width: 100%;
//     flex-direction: row;
//     justify-content: space-between;
//     flex-direction: row-reverse;
//   `};
// `

const ResponsiveButtonPurple = styled(ButtonPurple)`
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  padding: 9px 20px;
  width: fit-content;
  border: none;
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    flex: 1 1 auto;
    width: fit-content;
  `};
`

const ResponsiveButtonText = styled(ButtonPurple)`
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  padding: 9px 20px;
  width: fit-content;
  border: none;
  color: #ffffff;
  background: ${({ theme }) => theme.black1} !important;
  display: flex;
  align-items: center;
  gap: 3px;
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    flex: 1 1 auto;
    width: fit-content;
  `};
`

const NewPostionWrapper = styled.div`
  white-space: nowrap;
  /* ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
   display:none;
  `}; */
`

const BadgeText = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: ${({ theme }) => theme.textPrimary};
`

const FarmDetailsWrapper = styled(RowBetween)`
  @media only screen and (max-width: 720px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`

const InfoTitle = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.textTertiary};
  font-size: 12px;
  font-weight: 500;
`

function PositionsLoadingPlaceholder() {
  return (
    <LoadingRows>
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
    </LoadingRows>
  )
}

const FARM_DETAILS = gql`
  query p($id: String, $date_gt: Int) {
    poolDayDatas(
      first: 1000
      where: { pool: $id, date_gt: $date_gt }
      orderBy: date
      orderDirection: desc
      subgraphError: allow
    ) {
      date
      volumeUSD
      tvlUSD
      feesUSD
      txCount
      pool {
        feeTier
        token0Price
        token1Price
      }
    }
  }
`

const POOL_SEARCH = gql`
  query pool($id: String) {
    pool(id: $id) {
      token1 {
        derivedETH
      }
      token0 {
        derivedETH
      }
      totalValueLockedToken0
      totalValueLockedToken1
    }
  }
`

interface IFarmPrices {
  feesUSD: number
  volumeUSD: number
  token0Price: number
  token1Price: number
}

interface IDetails {
  token0: string
  token1: string
  fee: number
}

const FarmDatails = () => {
  const dispatch = useAppDispatch()
  const poolsState = useFarmPools()
  const list = useCombinedActiveList()
  const incentivesState = useFarmIncentives()
  const uniswapV3Staker = useUniswapV3Staker()
  const addTransactionWithCb = useTransactionAdderWithCallback()
  const pms = useV3ManagerContracts([SupportedChainId.XDC])
  const ethPrice = useEthPrices()
  const params = useParams()
  const theme = useTheme()
  const { account, chainId, provider } = useWeb3React()
  const { positions, loading: positionsLoading } = useV3Positions(account)
  const [stakedPositionsIds, setStakedPositionsIds] = useState<BigNumber[]>([])
  const [unStakedPositionsIds, setUnStakedPositionsIds] = useState<BigNumber[]>([])
  const [isFetchedStakedPositionsIds, setIsFetchedStakedPositionsIds] = useState(false)
  const [isFetchedUnStakedPositionsIds, setIsFetchedUnStakedPositionsIds] = useState(false)
  const [rewardInfoMap, setRewardInfoMap] = useState<Record<string, IRewardInfo>>({})
  const [unStakedRewardInfoMap, setUnstakedRewardInfoMap] = useState<Record<string, IRewardInfo>>({})
  const [isFetchedRewardInfoMap, setIsFetchedRewardInfoMap] = useState(false)
  const [isFetchedUnstakedRewardInfoMap, setIsFetchedUnstakedRewardInfoMap] = useState(false)
  const { positions: stakedPositions, loading: stakedPositionsLoading } = useV3PositionsFromTokenIds(stakedPositionsIds)
  const { positions: unStakedPositions, loading: unStakedPositionsLoading } =
    useV3PositionsFromTokenIds(unStakedPositionsIds)
  const toggleWalletDrawer = useToggleAccountDrawer()
  const [tvlEth, setTvlEth] = useState<number>(0)
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false)
  const [rewardsEth, setRewardsEth] = useState<BigNumber | null>(null)
  const [prices, setPrices] = useState<IFarmPrices>({
    feesUSD: 0,
    volumeUSD: 0,
    token0Price: 0,
    token1Price: 0,
  })
  const farm = useMemo(() => (params ? farms[Number(params.id)] : null), [params])

  const { incentiveKey, encoded, incentiveId } = useMemo(() => {
    if (!farm)
      return {
        incentiveKey: null,
        encoded: null,
        incentiveId: null,
      }

    const incentiveKey = {
      rewardToken: farm.rewardToken,
      pool: farm.pool,
      startTime: farm.startTime,
      endTime: farm.endTime,
      vestingPeriod: farm.vestingPeriod,
      refundee: farm.refundee,
    }

    const encoded = encodeIncentive(incentiveKey)
    const incentiveId = keccak256(encoded)

    return { incentiveKey, encoded, incentiveId }
  }, [farm])

  const [details, setDetails] = useState<IDetails>({
    fee: 0,
    token0: '',
    token1: '',
  })
  const token0 = useToken(details.token0)
  const token1 = useToken(details.token1)
  const rewardToken = useToken(incentiveKey?.rewardToken)

  const currency0 = token0 ? unwrappedToken(token0) : undefined
  const currency1 = token1 ? unwrappedToken(token1) : undefined

  const tvl = useMemo(() => {
    if (!ethPrice?.current || !tvlEth) {
      return 0
    }
    return ethPrice.current * tvlEth
  }, [ethPrice, tvlEth])

  const showConnectAWallet = Boolean(!account)

  const [openPositions, closedPositions] = useMemo(
    () =>
      positions?.reduce<[PositionDetails[], PositionDetails[]]>(
        (acc, p) => {
          acc[p.liquidity?.isZero() ? 1 : 0].push(p)
          return acc
        },
        [[], []]
      ) ?? [[], []],
    [positions]
  )

  const stakedInUSDT = useMemo(() => {
    if (!incentiveId || !ethPrice || incentivesState.status !== Status.SUCCESS) {
      return 0
    }
    const inXDC = incentivesState.data[incentiveId].stakedInXDC
    if (!inXDC) {
      return 0
    }
    return parseFloat(
      formatEther(
        inXDC.mul((parseFloat(ethPrice.current.toString()) * 1000000).toString().split('.')[0]).div('1000000')
      )
    )
  }, [ethPrice, incentivesState, incentiveId])

  const apr = useMemo(() => {
    if (
      !incentiveKey ||
      !incentiveId ||
      !incentivesState ||
      incentivesState.status !== Status.SUCCESS ||
      rewardsEth == null ||
      !farm ||
      !poolsState ||
      poolsState.status !== Status.SUCCESS ||
      !poolsState.data[farm.pool] ||
      !rewardToken
    ) {
      return 0
    }
    const time = incentiveKey.endTime - incentiveKey.startTime
    if (time === 0 || tvlEth == 0) {
      return 0
    }
    // const poolTvlInXdc = incentivesState.data[incentiveId].stakedInXDC
    // return Math.round(((((rewardsEth * ethPrice.current ?? 0) / time) * 60 * 60 * 24 * 365) / tvl) * 10000) / 100

    if (!tvlEth || tvlEth.toString() == '0') {
      return '> 10000'
    }

    const secondsInTheYear = '31536000'
    const inOneYear = rewardsEth
      .mul(poolsState.data[farm.pool].rewardTokenPrice)
      .div(1000)
      .mul(secondsInTheYear)
      .div(time)

    const res = formatUnits(inOneYear.mul(100).div(Math.round(tvlEth).toString()), rewardToken.decimals).split('.')
    if (res[0].length > 1) {
      return res[0]
    }
    return `${res[0]}.${res[1].slice(0, 2)}`
  }, [incentiveKey, incentiveId, rewardsEth, tvl, incentivesState, poolsState, rewardToken])

  const filteredPositions = useFilterPossiblyMaliciousPositions(openPositions)
  const filteredStakedPositions = useFilterPossiblyMaliciousPositions(stakedPositions || [])
  const filteredUnStakedPositions = useFilterPossiblyMaliciousPositions(unStakedPositions || [])

  const filteredPositionsByNFT = useMemo(() => {
    if (!details.token1 || !details.token0) return []

    return filteredPositions.filter((item) => {
      const t0 = item.token0 === details.token0 || item.token0 === details.token1
      const t1 = item.token1 === details.token0 || item.token1 === details.token1
      const fee = item.fee === details.fee

      return t0 && t1 && fee
    })
  }, [filteredPositions, details])

  const filteredStakedPositionsByNFT = useMemo(() => {
    if (!details.token1 || !details.token0) return []
    const unstakedTokenIds = filteredPositionsByNFT.map((item) => item.tokenId)
    return filteredStakedPositions.filter((item) => {
      const t0 = item.token0 === details.token0 || item.token0 === details.token1
      const t1 = item.token1 === details.token0 || item.token1 === details.token1
      const fee = item.fee === details.fee
      const positionIsUnstaked = unstakedTokenIds.includes(item.tokenId)

      return t0 && t1 && fee && !positionIsUnstaked
    })
  }, [filteredStakedPositions, filteredPositionsByNFT])

  const filteredUnStakedPositionsByNFT = useMemo(() => {
    if (!details.token1 || !details.token0) return []
    const unstakedTokenIds = filteredPositionsByNFT.map((item) => item.tokenId)
    return filteredUnStakedPositions.filter((item) => {
      const t0 = item.token0 === details.token0 || item.token0 === details.token1
      const t1 = item.token1 === details.token0 || item.token1 === details.token1
      const fee = item.fee === details.fee
      const positionIsUnstaked = unstakedTokenIds.includes(item.tokenId)
      //todo
      return t0 && t1 && fee && !positionIsUnstaked
    })
  }, [filteredUnStakedPositions, filteredPositionsByNFT])

  useEffect(() => {
    if (
      !filteredStakedPositionsByNFT ||
      filteredStakedPositionsByNFT.length === 0 ||
      !incentiveKey ||
      !uniswapV3Staker ||
      !rewardToken ||
      Object.values(rewardInfoMap).length > 0 ||
      isFetchedRewardInfoMap
    ) {
      return
    }
    setIsFetchedRewardInfoMap(true)
    Promise.all(
      filteredStakedPositionsByNFT.map((position) => uniswapV3Staker.getRewardInfo(incentiveKey, position.tokenId))
    ).then((res) => {
      const reduced: Record<string, IRewardInfo> = filteredStakedPositionsByNFT.reduce((prev, curr, index) => {
        return {
          ...prev,
          [curr.tokenId.toString()]: {
            current: formatUnits(res[index][0], rewardToken.decimals),
            max: formatUnits(res[index][1], rewardToken.decimals),
            token: rewardToken,
          },
        }
      }, {} as Record<string, IRewardInfo>)
      setRewardInfoMap(reduced)
    })
  }, [filteredStakedPositionsByNFT, rewardToken, rewardInfoMap, isFetchedRewardInfoMap])

  useEffect(() => {
    if (farm) {
      dispatch(fetchPools({ farms: [farm] }))
    }
  }, [farm])

  useEffect(() => {
    const fetchFarmDetails = async () => {
      if (
        !incentiveKey ||
        !provider ||
        !uniswapV3Staker ||
        !incentiveId ||
        !list ||
        !poolsState ||
        poolsState.status !== Status.SUCCESS
      )
        return

      const poolContract = getUniswapV3Pool(incentiveKey.pool, provider)
      if (!poolContract) return
      Promise.all([poolContract?.token0(), poolContract?.token1(), poolContract.fee()]).then(
        ([token0, token1, fee]) => {
          setDetails({
            token0,
            token1,
            fee,
          })
        }
      )
      const { data, loading } = await apolloClient.query<any>({
        query: FARM_DETAILS,
        variables: {
          id: incentiveKey.pool,
          date_gt: 1706734800,
        },
      })

      const {
        data: { pools },
        error,
      } = await apolloClient.query({
        query: POOLS_SEARCH,
        variables: {
          ids: [incentiveKey.pool],
        },
      })
      const pool: any = pools[0]
      dispatch(
        fetchIncentives({
          list,
          poolsMap: { [pool.id]: { ...pool, feeTier: convertToFeeTier(pool.feeTier) } },
          ids: [incentiveId],
        })
      )
      setTvlEth(
        parseFloat(pool.token1.derivedETH) * parseFloat(pool.totalValueLockedToken1) +
          parseFloat(pool.token0.derivedETH) * parseFloat(pool.totalValueLockedToken0)
      )

      const details = data.poolDayDatas[0]
      setPrices({
        feesUSD: details.feesUSD,
        volumeUSD: details.volumeUSD,
        token0Price: details.pool.token0Price,
        token1Price: details.pool.token1Price,
      })

      const { data: stakerData } = await apolloStakerClient.query<any>({
        query: STAKER_DETAILS,
        variables: {
          ids: [incentiveId],
        },
      })
      setRewardsEth(parseUnits(stakerData.incentives[0].reward, 0))
    }

    fetchFarmDetails()
  }, [provider, params, uniswapV3Staker, incentiveKey, incentiveId, list, poolsState])

  const fetchStakedPositions = useCallback(async () => {
    if (!incentiveId || !account) return

    const { data: stakedPositions } = await apolloStakerClient.query<any>({
      query: STAKED_POSITIONS,
      variables: {
        owner: account,
        incentiveId,
      },
      fetchPolicy: 'no-cache',
    })

    const tIds = stakedPositions.incentives[0].positions.map((item: any) => {
      return BigNumber.from(item.position.id)
    })

    setStakedPositionsIds(tIds)
  }, [incentiveId, account])

  const fetchUnStakedPositions = useCallback(async () => {
    if (!incentiveId || !account) return

    const { data: stakedPositions } = await apolloStakerClient.query<any>({
      query: UNSTAKED_POSITIONS,
      variables: {
        owner: account,
        incentiveId,
      },
      fetchPolicy: 'no-cache',
    })

    const tIds = stakedPositions.incentives[0].positions
      .filter((v: any) => {
        return v.position.claims.length !== v.position.unstakes.length
      })
      .map((item: any) => {
        return BigNumber.from(item.position.id)
      })

    setUnStakedPositionsIds(tIds)
  }, [incentiveId, account])

  useEffect(() => {
    if (stakedPositionsIds.length === 0 && !isFetchedStakedPositionsIds && incentiveId && account) {
      setIsFetchedStakedPositionsIds(true)
      fetchStakedPositions()
    }
  }, [incentiveId, account, stakedPositionsIds, isFetchedStakedPositionsIds])

  useEffect(() => {
    if (unStakedPositionsIds.length === 0 && !isFetchedUnStakedPositionsIds && incentiveId && account) {
      setIsFetchedUnStakedPositionsIds(true)
      fetchUnStakedPositions()
    }
  }, [incentiveId, account, unStakedPositionsIds, isFetchedUnStakedPositionsIds])

  const handleUnstake = async (tokenId: string) => {
    if (!uniswapV3Staker || !account || !incentiveKey || !rewardInfoMap[tokenId]) {
      return
    }

    const itf = new Interface(UNISWAP_V3_STAKER_ABI)
    const unstakeCallData = itf.encodeFunctionData('unstakeToken', [incentiveKey, tokenId])
    const withdrawCallData = itf.encodeFunctionData('withdrawToken', [tokenId, account, []])
    const claimCallData = itf.encodeFunctionData('claimReward', [
      incentiveKey.rewardToken,
      account,
      parseUnits(rewardInfoMap[tokenId].current, rewardToken?.decimals).toString(),
    ])

    const calls = [arrayify(unstakeCallData), arrayify(withdrawCallData), arrayify(claimCallData)]
    const res = await uniswapV3Staker.multicall(calls)

    addTransactionWithCb(
      res,
      {
        type: TransactionType.UNSTAKE,
        token0Address: token0?.address as string,
        token1Address: token1?.address as string,
        claimInfo: `${formatNumberOrString(rewardInfoMap[tokenId].current, NumberType.TokenNonTx)} ${
          rewardToken?.symbol
        }`,
      },
      () => {
        setTimeout(() => {
          fetchStakedPositions()
        }, 4000)
      }
    )
  }

  const handleWithdrawAndClaim = async (tokenId: string) => {
    if (!uniswapV3Staker || !account || !incentiveKey || !rewardToken) {
      return
    }
    const itf = new Interface(UNISWAP_V3_STAKER_ABI)
    const rewardAmount = await uniswapV3Staker.rewards(rewardToken.address, account)
    const withdrawCallData = itf.encodeFunctionData('withdrawToken', [tokenId, account, []])

    const claimCallData = itf.encodeFunctionData('claimReward', [
      incentiveKey.rewardToken,
      account,
      rewardAmount.toString(),
    ])

    const calls = [arrayify(withdrawCallData), arrayify(claimCallData)]
    const res = await uniswapV3Staker.multicall(calls)

    addTransactionWithCb(
      res,
      {
        type: TransactionType.UNSTAKE,
        token0Address: token0?.address as string,
        token1Address: token1?.address as string,
        claimInfo: `${formatNumberOrString(rewardAmount.toString(), NumberType.TokenNonTx)} ${rewardToken?.symbol}`,
      },
      () => {
        setTimeout(() => {
          fetchUnStakedPositions()
        }, 4000)
      }
    )
  }

  const timing = useMemo(() => {
    if (!incentiveKey) {
      return {
        title: '',
        content: '',
        color: '',
        farmIsEnded: false,
      }
    }
    if (incentiveKey.startTime * 1000 > Date.now()) {
      return {
        title: 'Starts at',
        content: format(incentiveKey.startTime * 1000, 'dd MMM yyyy HH:mm'),
        color: theme.blue600,
        farmIsEnded: false,
      }
    }
    if (incentiveKey.endTime * 1000 > Date.now()) {
      return {
        title: 'Ends at',
        content: format(incentiveKey.endTime * 1000, 'dd MMM yyyy HH:mm'),
        color: theme.green500,
        farmIsEnded: false,
      }
    }
    return {
      title: 'Ended at',
      content: format(incentiveKey.endTime * 1000, 'dd MMM yyyy HH:mm'),
      color: theme.red400,
      farmIsEnded: true,
    }
  }, [incentiveKey])

  const handleStake = async (tokenId: string) => {
    if (!account || !chainId || !pms || !pms[chainId] || !tokenId || !params || !params.id || !encoded) return

    try {
      const res = await pms[chainId]['safeTransferFrom(address,address,uint256,bytes)'](
        account,
        UNISWAP_V3_STAKER_ADDRESS[chainId],
        tokenId,
        encoded
      )
      addTransactionWithCb(
        res,
        {
          type: TransactionType.STAKE,
          token0Address: token0?.address as string,
          token1Address: token1?.address as string,
        },
        () => {
          setTimeout(() => {
            fetchStakedPositions()
          }, 4000)
        }
      )
    } catch (e) {
      console.error(e)
    }
  }
  const CurrencyLogos = useMemo(() => {
    if (currency0?.symbol?.toLowerCase() === 'wxdc' || currency0?.symbol?.toLowerCase() === 'xdc') {
      return (
        <>
          <DoubleCurrencyLogo currency0={currency1} currency1={currency0} size={24} margin={true} />
          <ThemedText.DeprecatedLabel fontSize="24px" mr="10px">
            &nbsp;{currency0?.symbol}&nbsp;/&nbsp;{currency1?.symbol} &nbsp;Farming
          </ThemedText.DeprecatedLabel>
        </>
      )
    }

    return (
      <>
        <DoubleCurrencyLogo currency0={currency0} currency1={currency1} size={24} margin={true} />
        <ThemedText.DeprecatedLabel fontSize="24px" mr="10px">
          &nbsp;{currency1?.symbol}&nbsp;/&nbsp;{currency0?.symbol} &nbsp;Farming
        </ThemedText.DeprecatedLabel>
      </>
    )
  }, [currency0, currency1])

  if (!isSupportedChain(chainId)) {
    return <WrongNetworkCard />
  }

  return (
    <PageWrapper>
      <AutoColumn gap="lg" justify="center">
        <AutoColumn gap="lg" style={{ width: '100%' }}>
          <Link
            data-cy="visit-farm"
            style={{ textDecoration: 'none', width: 'fit-content', marginBottom: '0.5rem' }}
            to="/farm"
          >
            <HoverText>
              <Trans>← Back to Farms</Trans>
            </HoverText>
          </Link>
          <FarmDetailsWrapper>
            <RowFixed>
              {CurrencyLogos}
              <Badge style={{ marginRight: '8px' }}>
                <BadgeText>
                  <Trans>{new Percent(details.fee, 1_000_000).toSignificant()}%</Trans>
                </BadgeText>
              </Badge>
            </RowFixed>
            <Row width="fit-content" gap="8px">
              <ResponsiveButtonText onClick={() => setShowInfoModal(true)}>
                <NewPostionWrapper>
                  <Trans>Info</Trans>
                </NewPostionWrapper>{' '}
                <InfoIconContainer>
                  <Info cursor="pointer" width={18} height={18} />
                </InfoIconContainer>
              </ResponsiveButtonText>
              <ResponsiveButtonPurple
                data-cy="join-pool-button"
                id="join-pool-button"
                as={Link}
                to={`/add/${details.token0}/${details.token1}/${details.fee}`}
              >
                +
                <NewPostionWrapper>
                  <Trans>New Position</Trans>
                </NewPostionWrapper>
              </ResponsiveButtonPurple>
            </Row>
          </FarmDetailsWrapper>

          <MainContentWrapper>
            <DesktopPoolSummaryHeader>
              <div>
                <Trans>Incentive info</Trans>
              </div>
            </DesktopPoolSummaryHeader>
            <PoolSummary>
              <div>
                <InfoTitle>Vesting period</InfoTitle>
                <div>{incentiveKey && getFormattedTime(incentiveKey.vestingPeriod * 1000)}</div>
              </div>
              <div>
                <InfoTitle>Total reward</InfoTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  {rewardsEth
                    ? formatNumber(
                        parseFloat(formatUnits(rewardsEth, rewardToken ? rewardToken.decimals : 18)),
                        NumberType.NFTCollectionStats
                      )
                    : '-'}
                  <ExternalLink
                    style={{ color: 'inherit' }}
                    href={getExplorerLink(chainId, rewardToken?.address as string, ExplorerDataType.TOKEN)}
                  >
                    {rewardToken?.symbol}
                  </ExternalLink>
                </div>
              </div>

              <div>
                <InfoTitle>
                  Reward apr
                  <MouseoverTooltip text="rewards_apr = (rewards_per_year / total_staked) * 100" placement="right">
                    <InfoIconContainer>
                      <Info size={14} />
                    </InfoIconContainer>
                  </MouseoverTooltip>
                </InfoTitle>
                <div>{apr}%</div>
              </div>
              <div style={{ borderLeft: `2px solid ${timing.color}`, paddingLeft: '5px' }}>
                <InfoTitle>{timing.title}</InfoTitle>
                <div>{timing.content}</div>
              </div>
            </PoolSummary>
            <PoolSummary>
              <div>
                <InfoTitle>Staked</InfoTitle>
                <div>{formatNumber(stakedInUSDT, NumberType.FiatTokenStats)}</div>
              </div>
              <div>
                <InfoTitle>Pool TVL</InfoTitle>
                <div>{formatNumber(tvl, NumberType.FiatTokenStats)}</div>
              </div>
              <div>
                <InfoTitle>Volume 24h</InfoTitle>
                <div>{formatNumber(prices.volumeUSD, NumberType.FiatTokenStats)}</div>
              </div>
              <div>
                <InfoTitle>24h fees</InfoTitle>
                <div>{formatNumber(prices?.feesUSD, NumberType.FiatTokenStats)}</div>
              </div>
              {/*<div>
                <InfoTitle>Current pool price</InfoTitle>
                <div>
                  {prices && token0 && token1
                    ? `${Number(prices?.token0Price).toFixed(3)} ${token0?.symbol}/${token1?.symbol}`
                    : '-'}
                </div>
              </div>*/}
            </PoolSummary>
          </MainContentWrapper>

          <TitleRow padding="0">
            <ThemedText.LargeHeader>
              <Trans>Staked positions</Trans>
            </ThemedText.LargeHeader>
          </TitleRow>
          <MainContentWrapper>
            {stakedPositionsLoading && details.token1 && details.token0 ? (
              <PositionsLoadingPlaceholder />
            ) : filteredStakedPositionsByNFT && filteredStakedPositionsByNFT.length > 0 ? (
              <PositionList
                incentiveKey={incentiveKey}
                rewardInfoMap={rewardInfoMap}
                positions={filteredStakedPositionsByNFT}
                setUserHideClosedPositions={() => null}
                userHideClosedPositions={true}
                hideClosedPositionsBtn={true}
                isStaked={true}
                handleStake={handleStake}
                handleUstake={handleUnstake}
              />
            ) : (
              <ErrorContainer>
                <ThemedText.DeprecatedBody color={theme.textTertiary} textAlign="center">
                  <InboxIcon strokeWidth={1} />
                  <div>
                    <Trans>You have no staked positions yet.Your staked V3 liquidity positions will appear here.</Trans>
                  </div>
                </ThemedText.DeprecatedBody>

                {showConnectAWallet && (
                  <ButtonPurple
                    style={{ marginTop: '1.25rem', padding: '9px 20px', width: 'auto' }}
                    onClick={toggleWalletDrawer}
                  >
                    <Trans>Connect a wallet</Trans>
                  </ButtonPurple>
                )}
              </ErrorContainer>
            )}
          </MainContentWrapper>

          {timing.farmIsEnded && (
            <>
              <TitleRow padding="0">
                <ThemedText.LargeHeader>
                  <Trans>Unstaked positions</Trans>
                </ThemedText.LargeHeader>
              </TitleRow>
              <MainContentWrapper>
                {unStakedPositionsLoading && details.token1 && details.token0 ? (
                  <PositionsLoadingPlaceholder />
                ) : filteredUnStakedPositionsByNFT && filteredUnStakedPositionsByNFT.length > 0 ? (
                  <PositionList
                    incentiveKey={incentiveKey}
                    rewardInfoMap={unStakedRewardInfoMap}
                    positions={filteredUnStakedPositionsByNFT}
                    setUserHideClosedPositions={() => null}
                    userHideClosedPositions={true}
                    hideClosedPositionsBtn={true}
                    isStaked={true}
                    isUnstaked={true}
                    handleStake={handleStake}
                    handleUstake={handleWithdrawAndClaim}
                  />
                ) : (
                  <ErrorContainer>
                    <ThemedText.DeprecatedBody color={theme.textTertiary} textAlign="center">
                      <InboxIcon strokeWidth={1} />
                      <div>
                        <Trans>
                          You have no unstaked positions yet.Your unstaked V3 liquidity positions will appear here.
                        </Trans>
                      </div>
                    </ThemedText.DeprecatedBody>

                    {showConnectAWallet && (
                      <ButtonPurple
                        style={{ marginTop: '1.25rem', padding: '9px 20px', width: 'auto' }}
                        onClick={toggleWalletDrawer}
                      >
                        <Trans>Connect a wallet</Trans>
                      </ButtonPurple>
                    )}
                  </ErrorContainer>
                )}
              </MainContentWrapper>
            </>
          )}

          <TitleRow padding="0">
            <ThemedText.LargeHeader>
              <Trans>Relevant positions (not staked)</Trans>
            </ThemedText.LargeHeader>
          </TitleRow>
          <MainContentWrapper>
            {positionsLoading ? (
              <PositionsLoadingPlaceholder />
            ) : filteredPositionsByNFT && filteredPositionsByNFT.length > 0 ? (
              <PositionList
                incentiveKey={incentiveKey}
                positions={filteredPositionsByNFT}
                setUserHideClosedPositions={() => null}
                userHideClosedPositions={true}
                hideClosedPositionsBtn={true}
                isStaked={false}
                handleStake={handleStake}
                handleUstake={handleUnstake}
                farmIsEnded={timing.farmIsEnded}
              />
            ) : (
              <ErrorContainer>
                <ThemedText.DeprecatedBody color={theme.textTertiary} textAlign="center">
                  <InboxIcon strokeWidth={1} />
                  <div>
                    <Trans>Your active V3 liquidity positions will appear here.</Trans>
                  </div>
                </ThemedText.DeprecatedBody>

                {showConnectAWallet && (
                  <ButtonPurple
                    style={{ marginTop: '1.25rem', padding: '9px 20px', width: 'auto' }}
                    onClick={toggleWalletDrawer}
                  >
                    <Trans>Connect a wallet</Trans>
                  </ButtonPurple>
                )}
              </ErrorContainer>
            )}
          </MainContentWrapper>
          <InfoModal isOpen={showInfoModal} onDismiss={() => setShowInfoModal(false)} />
        </AutoColumn>
      </AutoColumn>
    </PageWrapper>
  )
}

export default FarmDatails
